export const fetchSeasonData = async (
    id: string, // Can be plot_ID or NAZ_ORP
    thresholdKey: string,
    selectedYear: number,
    selectedDays: number | undefined, // Default undefined
    type: 'start' | 'end',
    dataType: 'plot' | 'orp' // New parameter to differentiate
): Promise<{ date: string | null; doy: number | null }> => {
    try {
        const days = selectedDays || 5; // Default duration if undefined
        const folder = dataType === 'orp' 
            ? (type === 'start' ? 'season_start_data_orp' : 'season_end_data_orp')
            : (type === 'start' ? 'season_start_data' : 'season_end_data');

        const filePath = `/${folder}/vegetation_season_${type}_${days}days/vegetation_season_${type}_${selectedYear}.csv`;

        const response = await fetch(filePath);
        if (!response.ok) {
            console.error(`Failed to fetch ${type} season data for ${dataType}:`, response.statusText);
            return { date: null, doy: null };
        }

        const text = await response.text();
        const rows = text.split('\n').map((row) => row.trim().split(','));

        if (rows.length < 2) {
            console.error('CSV file is empty or improperly formatted.');
            return { date: null, doy: null };
        }

        const headers = rows.shift();
        if (!headers) {
            console.error('CSV file has no headers.');
            return { date: null, doy: null };
        }

        const dateIndex = headers.indexOf(`${thresholdKey}_${type === 'start' ? 'Start_Date' : 'End_Date'}`);
        const doyIndex = headers.indexOf(`${thresholdKey}_${type === 'start' ? 'Start_DOY' : 'End_DOY'}`);

        if (dateIndex === -1 || doyIndex === -1) {
            console.error(`Columns for ${thresholdKey} not found in CSV headers. Available headers:`, headers);
            return { date: null, doy: null };
        }

        const dataRow = rows.find((row) => row[0] === String(id));
        if (!dataRow) {
            console.warn(`No data found for ${dataType} ID '${id}'.`);
            return { date: null, doy: null };
        }

        const rawDate = dataRow[dateIndex]?.trim();
        const doyValue = parseFloat(dataRow[doyIndex]?.trim()) || null;

        const formattedDate = rawDate
            ? `${selectedYear}-${rawDate.split('-')[0].padStart(2, '0')}-${rawDate.split('-')[1].padStart(2, '0')}`
            : null;

        return { date: formattedDate, doy: doyValue };
    } catch (error) {
        console.error(`Error fetching ${type} season data for ${dataType}:`, error);
        return { date: null, doy: null };
    }
};



export const fetchRemoteSensingData = async (
        year: number
    ): Promise<Record<string, { start: number | null, end: number | null }>> => {
        const [startText, endText] = await Promise.all([
            fetch(`/remote_sensing/start_of_season/${year}.csv`).then(res => res.ok ? res.text() : ""),
            fetch(`/remote_sensing/end_of_season/${year}.csv`).then(res => res.ok ? res.text() : "")
        ]);

        const parseCsv = (csv: string): Record<string, number | null> => {
            const lines = csv.trim().split('\n').slice(1); // skip header
            const data: Record<string, number | null> = {};

            for (const line of lines) {
                const columns = line.split(',').map(s => s.replace(/^"|"$/g, '').trim());
                if (columns.length < 2) continue;

                const site = columns[0];
                const doyStr = columns[1];
                const doy = doyStr?.toUpperCase() === "NA" ? null : parseInt(doyStr, 10);

                if (site) {
                    data[site] = !isNaN(doy ?? NaN) ? doy : null;
                }
            }

            return data;
        };

        const startMap = startText ? parseCsv(startText) : {};
        const endMap = endText ? parseCsv(endText) : {};

        const allSites = new Set([...Object.keys(startMap), ...Object.keys(endMap)]);
        const combined: Record<string, { start: number | null, end: number | null }> = {};

        allSites.forEach(site => {
            combined[site] = {
                start: startMap[site] ?? null,
                end: endMap[site] ?? null
            };
        });

        return combined;
    };

    export const fetchCombinedProductData = async (
        year: number
    ): Promise<Record<string, { start: number | null, end: number | null }>> => {
        const [startText, endText] = await Promise.all([
            fetch(`/combined/start_of_season/${year}.csv`).then(res => res.ok ? res.text() : ""),
            fetch(`/combined/end_of_season/${year}.csv`).then(res => res.ok ? res.text() : "")
        ]);

        const parseCsv = (csv: string): Record<string, number | null> => {
            const lines = csv.trim().split('\n').slice(1); // skip header
            const data: Record<string, number | null> = {};

            for (const line of lines) {
                const columns = line.split(',').map(s => s.replace(/^"|"$/g, '').trim());
                if (columns.length < 2) continue;

                const site = columns[0]; // corresponds to plot_name
                const doyStr = columns[1];
                const doy = doyStr?.toUpperCase() === "NA" ? null : parseInt(doyStr, 10);

                if (site) {
                    data[site] = !isNaN(doy ?? NaN) ? doy : null;
                }
            }

            return data;
        };

        const startMap = startText ? parseCsv(startText) : {};
        const endMap = endText ? parseCsv(endText) : {};

        const allSites = new Set([...Object.keys(startMap), ...Object.keys(endMap)]);
        const combined: Record<string, { start: number | null, end: number | null }> = {};

        allSites.forEach(site => {
            combined[site] = {
                start: startMap[site] ?? null,
                end: endMap[site] ?? null
            };
        });

        return combined;
    };